import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { treeToHtml, htmlToTree } from '@/utils/sitemapParser';

const SitemapEditor = ({ isOpen, onClose, currentData, menuTitle, onSave }) => {
  const [editorHtml, setEditorHtml] = useState('');
  const [error, setError] = useState('');

  // Load data into editor
  useEffect(() => {
    if (isOpen && currentData) {
      setEditorHtml(treeToHtml(currentData));
    }
  }, [isOpen, currentData]);

  const handleSave = () => {
    try {
      const newTree = htmlToTree(editorHtml);
      if (newTree) {
        onSave(newTree);
        onClose();
      } else {
        setError('Sitemap cannot be empty.');
      }
    } catch (err) {
      setError('Failed to parse sitemap. Please check the structure.');
      console.error(err);
    }
  };

  // Editor Configuration
  const modules = {
    toolbar: [
      ['bold', 'italic'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['clean']
    ],
    clipboard: {
      // Toggle to standard matchers if needed
      matchVisual: false
    }
  };

  const formats = [
    'bold', 'italic',
    'list', 'bullet', 'indent'
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />
          
          {/* Modal Content */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }} 
            exit={{ opacity: 0, scale: 0.95, y: 20 }} 
            className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] flex-1"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Edit {menuTitle}</h2>
                <p className="text-xs text-slate-500">Edit your sitemap visually. Use the toolbar to format.</p>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            {/* Editor Area */}
            <div className="flex-1 p-6 overflow-y-auto bg-white flex flex-col">
               <div className="flex-1 h-full min-h-[300px]">
                <ReactQuill 
                  theme="snow"
                  value={editorHtml}
                  onChange={setEditorHtml}
                  modules={modules}
                  formats={formats}
                  className="h-full flex flex-col"
                  placeholder="Start typing your sitemap here..."
                />
               </div>
               
               {/* Custom Styles for Quill to match design */}
               <style>{`
                 .quill { display: flex; flex-direction: column; height: 100%; }
                 .ql-container { flex: 1; font-size: 16px; font-family: inherit; border-bottom-left-radius: 0.5rem; border-bottom-right-radius: 0.5rem; }
                 .ql-toolbar { border-top-left-radius: 0.5rem; border-top-right-radius: 0.5rem; background: #f8fafc; border-color: #e2e8f0 !important; }
                 .ql-container.ql-snow { border-color: #e2e8f0 !important; }
                 .ql-editor { font-family: inherit; color: #334155; }
               `}</style>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center shrink-0">
              <div className="text-red-500 text-sm font-medium">{error}</div>
              <div className="flex gap-3">
                <button 
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200/50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSave}
                  className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-lg shadow-lg shadow-indigo-200 transition-all flex items-center gap-2"
                >
                  <SafeIcon icon={FiIcons.FiCheck} />
                  Update Flowchart
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SitemapEditor;